// THIS IS CURRENTLY ONLY FOR KIDS & CO IT WILL BE UPDATED IN FUTURE FOR GLOBAL
import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { LocationOn } from '@mui/icons-material';
import axios from 'axios';
import logoImage from './logo.png';
import northAmericaImage from './north-america.svg';
import colors from '../../utils/colors';
import json2mq from 'json2mq';
import { SESSION_KEY_LOCATION_ID } from '../../constants';

interface GetLocationsResponse {
  data: Location[];
}

const eventsApiUrl = import.meta.env.VITE_EVENTS_API_URL;

const getLocationsWithDetails = async (clientId: string) => {
  try {
    const axiosConfig = {
      baseURL: eventsApiUrl,
      headers: {
        client_id: clientId,
      },
    };

    const response = await axios.request<GetLocationsResponse>({
      url: '/locations/list/details',
      method: 'get',
      ...axiosConfig,
    });
    return response;
  } catch (error) {
    console.error(error);
  }
};

function getDistanceFromLatLng(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLng = deg2rad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in km

  return distance;
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

function findClosestLocation(userLat: number, userLng: number, locations: any) {
  let closestLocation = null;
  let minDistance = Infinity;

  // Loop through all locations to find the closest one
  for (const location of locations) {
    const lat = parseFloat(location.latitude);
    const lng = parseFloat(location.longitude);

    const distance = getDistanceFromLatLng(userLat, userLng, lat, lng);

    if (distance < minDistance) {
      minDistance = distance;
      closestLocation = location;
    }
  }

  return {
    location: closestLocation,
    distance: minDistance,
  };
}
type Location = {
  uuid: string;
  region: string;
  city: string;
  name: string;
  latitude: string;
  longitude: string;
};

function nameSort(a: { name: string }, b: { name: string }) {
  if (a.name === b.name) return 0;
  return a.name > b.name ? 1 : -1;
}

function getLocationsGrouped(locations: Location[]) {
  const groupedLocations: any = {};
  locations.forEach(({ region, city, ...location }) => {
    if (!groupedLocations[region]) groupedLocations[region] = {};
    if (!groupedLocations[region][city]) groupedLocations[region][city] = [];
    groupedLocations[region][city].push(location);
  });

  const finalGroup: any = [];
  Object.entries(groupedLocations).forEach(([regionName, cityData]: [string, any]) => {
    const citiesForThisRegion: any[] = [];
    Object.entries(cityData).forEach(([cityName, locations]: [string, any]) => {
      citiesForThisRegion.push({ name: cityName, locations: locations.sort(nameSort) });
    });
    finalGroup.push({ name: regionName, cities: citiesForThisRegion.sort(nameSort) });
  });

  return finalGroup.sort(nameSort);
}

const clientId = 'd293fddf-bcac-11ef-8bcb-028d36469a89';
const currentRedirectLink = `/?client_id=${clientId}`;
export default function EventLocationPicker() {
  const [locations, setLocations] = React.useState<Location[]>([]);
  const [groupedLocations, setGroupedLocations] = React.useState<
    {
      name: string;
      cities: { name: string; locations: Location[] }[];
    }[]
  >();
  const [selectedLocationUUID, setSelectedLocationUUID] = React.useState(
    localStorage.getItem(SESSION_KEY_LOCATION_ID) || 'default',
  );
  const [redirectLink, setRedirectLink] = React.useState('');

  const [loading, setLoading] = React.useState(false);
  const isDesktop = useMediaQuery(
    json2mq({
      minWidth: 600,
    }),
  );

  React.useEffect(() => {
    (async () => {
      const response = await getLocationsWithDetails(clientId);
      if (!response?.data?.data) return;
      setGroupedLocations(getLocationsGrouped(response.data.data));
      setLocations(response.data.data);
    })();
  }, []);

  React.useEffect(() => {
    if (selectedLocationUUID !== 'default')
      setRedirectLink(`${currentRedirectLink}&location_id=${selectedLocationUUID}`);
  }, [selectedLocationUUID]);
  if (!groupedLocations) return null;

  function getLocation(locations: Location[]) {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position: any) => success(position, locations, setLoading), error);
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  }

  const success = (position: any, locations: Location[], setLoading: any) => {
    const myLocation = { latitude: position.coords.latitude, longitude: position.coords.longitude };

    let closestLocation = findClosestLocation(myLocation.latitude, myLocation.longitude, locations);

    setSelectedLocationUUID(closestLocation.location.uuid);
    setLoading(false);
  };

  function error() {
    alert('Sorry, no position available.');
  }

  return (
    <Box
      width="100vw"
      height="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        backgroundImage: `url(${northAmericaImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundColor: '#196788',
      }}
      fontSize={isDesktop ? '12px' : '10px'}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        // border="1px solid black"
        borderRadius={'20px'}
        sx={{ backgroundColor: 'white', boxShadow: 5 }}
        paddingTop="4em"
        paddingBottom="1em"
        paddingLeft="1.2em"
        paddingRight="1.2em"
      >
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <img src={logoImage} alt={'company-logo'} loading="lazy" width="63" />
          <Typography variant="h5" paddingTop="0.5em" fontSize="2em">
          {`${new Date().getFullYear()}`} Summer Camp Registration
          </Typography>
          <Typography variant="caption" fontSize="1.2em">
            Powered By Kindertales
          </Typography>
          <Typography variant="h6" padding="0.5em 0em" fontSize="1.6em">
            Please select your location below:
          </Typography>
          <Box display="flex" gap="1em" alignItems="center" justifyContent="center">
            <Typography fontSize="1.34em">
              <Link
                type="button"
                onClick={() => getLocation(locations)}
                color={colors.blue.link}
                sx={{ cursor: 'pointer' }}
              >
                <LocationOn />
                Find My Location
              </Link>
            </Typography>
            {loading && <CircularProgress size="1.33em" />}
          </Box>
          <FormControl sx={{ m: 1, width: '90%' }} color="primary">
            <InputLabel htmlFor="grouped-select">Location</InputLabel>
            <Select
              defaultValue=""
              id="grouped-select"
              label="Location"
              value={selectedLocationUUID}
              onChange={(event) => {
                setSelectedLocationUUID(event.target.value);
              }}
            >
              <MenuItem value="default" selected={selectedLocationUUID == 'default'}>
                Select Your Location
              </MenuItem>
              {groupedLocations.map((region) => {
                return [
                  <ListSubheader>{region.name}</ListSubheader>,
                  ...region.cities.map((city) => {
                    return [
                      <ListSubheader sx={{ marginLeft: '1em' }}>{city.name}</ListSubheader>,
                      ...city.locations.map((location) => {
                        return (
                          <MenuItem value={location.uuid} selected={selectedLocationUUID === location.uuid}>
                            {location.name}
                          </MenuItem>
                        );
                      }),
                    ];
                  }),
                ];
              })}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            disabled={!redirectLink}
            sx={{ backgroundColor: colors.primary.main, ':hover': { backgroundColor: colors.primary.main } }}
            onClick={() => {
              window.location.href = `https://events.kindertales.com${redirectLink}`;
            }}
          >
            TAKE ME THERE
          </Button>
          <Typography paddingTop="3em" fontSize="1.6em">
            Copyright Kindertales {`${new Date().getFullYear()} `}
          </Typography>
          <Box display="fex" gap="0.5em">
            <Typography fontSize="1.6em">
              <Link
                color="info"
                variant="inherit"
                underline="hover"
                href="https://www.kindertales.com/terms-of-service"
                target="_blank"
              >
                Terms of Service
              </Link>
            </Typography>
            <Typography>•</Typography>
            <Typography fontSize="1.6em">
              <Link
                color="info"
                variant="inherit"
                underline="hover"
                href="https://www.kindertales.com/privacy-policy"
                target="_blank"
              >
                Privacy Policy
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
